import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  TrendingUp, TrendingDown, ArrowRight, Wallet, Shield,
  Zap, BarChart3, LineChart, CandlestickChart, Activity,
  Clock, Percent, Globe, ChevronRight,
  ArrowUpRight, ArrowDownRight, Loader
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import WalletConnectModal from '../components/WalletConnectModal';
import { useAuth } from '../App';
import toast from 'react-hot-toast';
import './Trading.css';

// Number of coins to fetch and display
const INITIAL_COINS = 6;
const COINS_PER_LOAD = 6;
const MAX_COINS = 50;

const tradingFeatures = [
  {
    icon: CandlestickChart,
    title: 'Spot Trading',
    description: 'Trade 500+ cryptocurrencies with deep liquidity and tight spreads.',
    tag: 'Most Popular'
  },
  {
    icon: LineChart,
    title: 'Margin Trading',
    description: 'Amplify your trades with up to 10x leverage on major pairs.',
    tag: 'Advanced'
  },
  {
    icon: BarChart3,
    title: 'Futures',
    description: 'Trade perpetual contracts with up to 100x leverage.',
    tag: 'Pro'
  },
  {
    icon: Activity,
    title: 'Copy Trading',
    description: 'Follow top traders and automatically copy their strategies.',
    tag: 'New'
  }
];

const whyTradeFeatures = [
  {
    icon: Zap,
    value: '<0.1s',
    label: 'Order Execution',
    description: 'Lightning-fast trades with minimal slippage'
  },
  {
    icon: Percent,
    value: '0.1%',
    label: 'Trading Fees',
    description: 'Industry-leading low fees, even lower with VIP'
  },
  {
    icon: Shield,
    value: '100%',
    label: 'Secure',
    description: 'Cold storage & multi-sig protection'
  },
  {
    icon: Globe,
    value: '24/7',
    label: 'Support',
    description: 'Round-the-clock customer assistance'
  }
];

const Trading = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [allCoins, setAllCoins] = useState([]);
  const [visibleCoins, setVisibleCoins] = useState(INITIAL_COINS);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [btcPrice, setBtcPrice] = useState(null);
  const [activeTab, setActiveTab] = useState('all');
  const { registerUser } = useAuth();
  const navigate = useNavigate();

  // Filter coins based on active tab
  const getFilteredCoins = () => {
    switch (activeTab) {
      case 'gainers':
        return [...allCoins].filter(c => c.rawChange > 0).sort((a, b) => b.rawChange - a.rawChange);
      case 'losers':
        return [...allCoins].filter(c => c.rawChange < 0).sort((a, b) => a.rawChange - b.rawChange);
      case 'new':
        return allCoins.slice(20, 50); // Simulate new listings from lower market cap
      case 'popular':
        return allCoins.slice(0, 10); // Top 10 by market cap
      default:
        return allCoins;
    }
  };

  const filteredCoins = getFilteredCoins();

  // Get currently displayed trading pairs
  const tradingPairs = filteredCoins.slice(0, visibleCoins);
  const hasMoreCoins = visibleCoins < filteredCoins.length;

  // Reset visible coins when tab changes
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setVisibleCoins(INITIAL_COINS);
  };

  // Load more coins
  const handleLoadMore = () => {
    setIsLoadingMore(true);
    setTimeout(() => {
      setVisibleCoins(prev => Math.min(prev + COINS_PER_LOAD, allCoins.length));
      setIsLoadingMore(false);
    }, 500);
  };

  // Fetch real-time prices and logos from CoinGecko API
  useEffect(() => {
    const fetchPrices = async () => {
      try {
        // Fetch top coins by market cap with images
        const response = await fetch(
          `https://api.coingecko.com/api/v3/coins/markets?vs_currency=usd&order=market_cap_desc&per_page=${MAX_COINS}&page=1&sparkline=false&price_change_percentage=24h`,
          {
            method: 'GET',
            headers: {
              'Accept': 'application/json',
            }
          }
        );
        
        if (!response.ok) {
          throw new Error('API request failed');
        }
        
        const data = await response.json();
        
        const formattedData = data.map(coin => {
          const price = coin.current_price;
          const change = coin.price_change_percentage_24h || 0;
          const volume = coin.total_volume || 0;
          return {
            id: coin.id,
            name: coin.name,
            symbol: coin.symbol.toUpperCase(),
            pair: `${coin.symbol.toUpperCase()}/USDT`,
            price: price.toLocaleString('en-US', { 
              minimumFractionDigits: 2, 
              maximumFractionDigits: price < 1 ? 4 : 2 
            }),
            change: `${change >= 0 ? '+' : ''}${change.toFixed(2)}%`,
            volume: formatVolume(volume),
            isUp: change >= 0,
            logo: coin.image, // Logo from API
            rawPrice: price,
            rawChange: change
          };
        });
        
        setAllCoins(formattedData);
        
        // Set BTC price for chart display (first item is usually BTC)
        const btc = data.find(c => c.id === 'bitcoin');
        if (btc) {
          setBtcPrice({
            current_price: btc.current_price,
            price_change_percentage_24h: btc.price_change_percentage_24h,
            image: btc.image
          });
        }
        
        setIsLoading(false);
      } catch (error) {
        console.error('Error fetching prices:', error);
        // Use fallback static data if API fails
        const fallbackData = [
          { pair: 'BTC/USDT', symbol: 'BTC', name: 'Bitcoin', price: '84,532.00', change: '+1.24%', volume: '28.5B', isUp: true, logo: 'https://assets.coingecko.com/coins/images/1/small/bitcoin.png', rawPrice: 84532 },
          { pair: 'ETH/USDT', symbol: 'ETH', name: 'Ethereum', price: '3,215.40', change: '+0.87%', volume: '12.3B', isUp: true, logo: 'https://assets.coingecko.com/coins/images/279/small/ethereum.png', rawPrice: 3215.40 },
          { pair: 'USDT/USD', symbol: 'USDT', name: 'Tether', price: '1.00', change: '+0.01%', volume: '45.2B', isUp: true, logo: 'https://assets.coingecko.com/coins/images/325/small/Tether.png', rawPrice: 1.00 },
          { pair: 'BNB/USDT', symbol: 'BNB', name: 'BNB', price: '598.20', change: '+0.45%', volume: '1.8B', isUp: true, logo: 'https://assets.coingecko.com/coins/images/825/small/bnb-icon2_2x.png', rawPrice: 598.20 },
          { pair: 'SOL/USDT', symbol: 'SOL', name: 'Solana', price: '142.65', change: '-2.15%', volume: '3.2B', isUp: false, logo: 'https://assets.coingecko.com/coins/images/4128/small/solana.png', rawPrice: 142.65 },
          { pair: 'XRP/USDT', symbol: 'XRP', name: 'XRP', price: '0.5234', change: '+3.21%', volume: '2.1B', isUp: true, logo: 'https://assets.coingecko.com/coins/images/44/small/xrp-symbol-white-128.png', rawPrice: 0.5234 },
          { pair: 'DOGE/USDT', symbol: 'DOGE', name: 'Dogecoin', price: '0.0823', change: '-1.45%', volume: '890M', isUp: false, logo: 'https://assets.coingecko.com/coins/images/5/small/dogecoin.png', rawPrice: 0.0823 },
          { pair: 'ADA/USDT', symbol: 'ADA', name: 'Cardano', price: '0.4521', change: '+2.34%', volume: '520M', isUp: true, logo: 'https://assets.coingecko.com/coins/images/975/small/cardano.png', rawPrice: 0.4521 },
          { pair: 'AVAX/USDT', symbol: 'AVAX', name: 'Avalanche', price: '25.87', change: '-0.92%', volume: '380M', isUp: false, logo: 'https://assets.coingecko.com/coins/images/12559/small/Avalanche_Circle_RedWhite_Trans.png', rawPrice: 25.87 },
          { pair: 'LINK/USDT', symbol: 'LINK', name: 'Chainlink', price: '14.52', change: '+1.78%', volume: '450M', isUp: true, logo: 'https://assets.coingecko.com/coins/images/877/small/chainlink-new-logo.png', rawPrice: 14.52 },
          { pair: 'DOT/USDT', symbol: 'DOT', name: 'Polkadot', price: '7.23', change: '+0.65%', volume: '320M', isUp: true, logo: 'https://assets.coingecko.com/coins/images/12171/small/polkadot.png', rawPrice: 7.23 },
          { pair: 'MATIC/USDT', symbol: 'MATIC', name: 'Polygon', price: '0.8934', change: '+1.12%', volume: '280M', isUp: true, logo: 'https://assets.coingecko.com/coins/images/4713/small/matic-token-icon.png', rawPrice: 0.8934 },
          { pair: 'SHIB/USDT', symbol: 'SHIB', name: 'Shiba Inu', price: '0.00001234', change: '+5.67%', volume: '650M', isUp: true, logo: 'https://assets.coingecko.com/coins/images/11939/small/shiba.png', rawPrice: 0.00001234 },
          { pair: 'TRX/USDT', symbol: 'TRX', name: 'TRON', price: '0.1234', change: '+0.89%', volume: '420M', isUp: true, logo: 'https://assets.coingecko.com/coins/images/1094/small/tron-logo.png', rawPrice: 0.1234 },
          { pair: 'UNI/USDT', symbol: 'UNI', name: 'Uniswap', price: '9.87', change: '-0.45%', volume: '180M', isUp: false, logo: 'https://assets.coingecko.com/coins/images/12504/small/uni.jpg', rawPrice: 9.87 },
          { pair: 'ATOM/USDT', symbol: 'ATOM', name: 'Cosmos', price: '8.45', change: '+2.12%', volume: '210M', isUp: true, logo: 'https://assets.coingecko.com/coins/images/1481/small/cosmos_hub.png', rawPrice: 8.45 },
          { pair: 'LTC/USDT', symbol: 'LTC', name: 'Litecoin', price: '72.34', change: '+1.23%', volume: '340M', isUp: true, logo: 'https://assets.coingecko.com/coins/images/2/small/litecoin.png', rawPrice: 72.34 },
          { pair: 'ETC/USDT', symbol: 'ETC', name: 'Ethereum Classic', price: '18.92', change: '-1.78%', volume: '150M', isUp: false, logo: 'https://assets.coingecko.com/coins/images/453/small/ethereum-classic-logo.png', rawPrice: 18.92 },
        ];
        setAllCoins(fallbackData);
        setBtcPrice({ current_price: 84532, price_change_percentage_24h: 1.24, image: 'https://assets.coingecko.com/coins/images/1/small/bitcoin.png' });
        setIsLoading(false);
      }
    };

    fetchPrices();
    // Refresh prices every 60 seconds (to avoid rate limiting)
    const interval = setInterval(fetchPrices, 60000);
    return () => clearInterval(interval);
  }, []);

  // Format volume to readable format (B, M, K)
  const formatVolume = (volume) => {
    if (volume >= 1e9) return `${(volume / 1e9).toFixed(1)}B`;
    if (volume >= 1e6) return `${(volume / 1e6).toFixed(0)}M`;
    if (volume >= 1e3) return `${(volume / 1e3).toFixed(0)}K`;
    return volume.toString();
  };

  const handleWalletConnect = async (walletData) => {
    try {
      await registerUser(walletData);
      sessionStorage.setItem('walletJustConnected', 'true');
      setIsModalOpen(false);
      toast.success('Account created successfully!');
      navigate('/backup-success');
    } catch (error) {
      toast.error('Could not create account. Please try again.');
    }
  };

  return (
    <div className="trading-page">
      <Navbar />
      
      {/* Hero Section */}
      <section className="trading-hero">
        <div className="hero-glow"></div>
        <div className="container">
          <motion.div 
            className="trading-hero-content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="hero-badge">
              <Activity size={14} />
              Trade Smarter
            </span>
            <h1>Trade Crypto<br /><span className="text-gradient">Like a Pro</span></h1>
            <p className="hero-subtitle">
              <span className="highlight">500+ pairs</span> • Lightning-fast execution • <span className="highlight">0.1% fees</span>
            </p>
            <p className="hero-description">
              Experience seamless trading with deep liquidity and professional-grade tools.
            </p>
            
            <div className="hero-actions">
              <button 
                className="btn btn-primary btn-lg"
                onClick={() => setIsModalOpen(true)}
              >
                Start Trading
                <ArrowRight size={20} />
              </button>
              <button 
                className="btn btn-secondary btn-lg"
                onClick={() => document.getElementById('markets-section').scrollIntoView({ behavior: 'smooth' })}
              >
                View Markets
              </button>
            </div>

            <div className="hero-stats-row">
              <div className="hero-mini-stat">
                <span className="mini-stat-value">$50B+</span>
                <span className="mini-stat-label">24h Volume</span>
              </div>
              <div className="stat-divider"></div>
              <div className="hero-mini-stat">
                <span className="mini-stat-value">500+</span>
                <span className="mini-stat-label">Trading Pairs</span>
              </div>
              <div className="stat-divider"></div>
              <div className="hero-mini-stat">
                <span className="mini-stat-value">10M+</span>
                <span className="mini-stat-label">Active Traders</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="categories-section">
        <div className="container">
          <div className="categories-grid">
            {/* Hot Tokens */}
            <motion.div 
              className="category-box"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="category-header">
                <span className="category-icon hot">🔥</span>
                <h3>Hot Tokens</h3>
              </div>
              <div className="category-list">
                {allCoins.slice(0, 3).map((coin, index) => (
                  <div key={coin.symbol} className="category-coin">
                    <div className="coin-rank">{index + 1}</div>
                    <img src={coin.logo} alt={coin.name} className="coin-logo" />
                    <div className="coin-info">
                      <span className="coin-symbol">{coin.symbol}</span>
                      <span className="coin-name">{coin.name}</span>
                    </div>
                    <div className="coin-price-data">
                      <span className="coin-price">${coin.price}</span>
                      <span className={`coin-change ${coin.isUp ? 'up' : 'down'}`}>{coin.change}</span>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Top Movers */}
            <motion.div 
              className="category-box"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <div className="category-header">
                <span className="category-icon movers">🚀</span>
                <h3>Top Movers</h3>
              </div>
              <div className="category-list">
                {[...allCoins].sort((a, b) => Math.abs(b.rawChange || 0) - Math.abs(a.rawChange || 0)).slice(0, 3).map((coin, index) => (
                  <div key={coin.symbol} className="category-coin">
                    <div className="coin-rank">{index + 1}</div>
                    <img src={coin.logo} alt={coin.name} className="coin-logo" />
                    <div className="coin-info">
                      <span className="coin-symbol">{coin.symbol}</span>
                      <span className="coin-name">{coin.name}</span>
                    </div>
                    <div className="coin-price-data">
                      <span className="coin-price">${coin.price}</span>
                      <span className={`coin-change ${coin.isUp ? 'up' : 'down'}`}>{coin.change}</span>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Newly Listed */}
            <motion.div 
              className="category-box"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <div className="category-header">
                <span className="category-icon new">✨</span>
                <h3>Newly Listed</h3>
              </div>
              <div className="category-list">
                {allCoins.slice(15, 18).length > 0 ? allCoins.slice(15, 18).map((coin, index) => (
                  <div key={coin.symbol} className="category-coin">
                    <div className="coin-rank new-badge">NEW</div>
                    <img src={coin.logo} alt={coin.name} className="coin-logo" />
                    <div className="coin-info">
                      <span className="coin-symbol">{coin.symbol}</span>
                      <span className="coin-name">{coin.name}</span>
                    </div>
                    <div className="coin-price-data">
                      <span className="coin-price">${coin.price}</span>
                      <span className={`coin-change ${coin.isUp ? 'up' : 'down'}`}>{coin.change}</span>
                    </div>
                  </div>
                )) : allCoins.slice(6, 9).map((coin, index) => (
                  <div key={coin.symbol} className="category-coin">
                    <div className="coin-rank new-badge">NEW</div>
                    <img src={coin.logo} alt={coin.name} className="coin-logo" />
                    <div className="coin-info">
                      <span className="coin-symbol">{coin.symbol}</span>
                      <span className="coin-name">{coin.name}</span>
                    </div>
                    <div className="coin-price-data">
                      <span className="coin-price">${coin.price}</span>
                      <span className={`coin-change ${coin.isUp ? 'up' : 'down'}`}>{coin.change}</span>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Buzzing on X */}
            <motion.div 
              className="category-box"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
            >
              <div className="category-header">
                <span className="category-icon buzzing">𝕏</span>
                <h3>Buzzing on X</h3>
              </div>
              <div className="category-list">
                {allCoins.filter(c => ['DOGE', 'SHIB', 'SOL', 'PEPE', 'BTC', 'XRP'].includes(c.symbol)).slice(0, 3).length > 0 
                  ? allCoins.filter(c => ['DOGE', 'SHIB', 'SOL', 'PEPE', 'BTC', 'XRP'].includes(c.symbol)).slice(0, 3).map((coin, index) => (
                    <div key={coin.symbol} className="category-coin">
                      <div className="coin-rank trending">📈</div>
                      <img src={coin.logo} alt={coin.name} className="coin-logo" />
                      <div className="coin-info">
                        <span className="coin-symbol">{coin.symbol}</span>
                        <span className="coin-name">{coin.name}</span>
                      </div>
                      <div className="coin-price-data">
                        <span className="coin-price">${coin.price}</span>
                        <span className={`coin-change ${coin.isUp ? 'up' : 'down'}`}>{coin.change}</span>
                      </div>
                    </div>
                  ))
                  : allCoins.slice(3, 6).map((coin, index) => (
                    <div key={coin.symbol} className="category-coin">
                      <div className="coin-rank trending">📈</div>
                      <img src={coin.logo} alt={coin.name} className="coin-logo" />
                      <div className="coin-info">
                        <span className="coin-symbol">{coin.symbol}</span>
                        <span className="coin-name">{coin.name}</span>
                      </div>
                      <div className="coin-price-data">
                        <span className="coin-price">${coin.price}</span>
                        <span className={`coin-change ${coin.isUp ? 'up' : 'down'}`}>{coin.change}</span>
                      </div>
                    </div>
                  ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Live Markets Section */}
      <section className="markets-section" id="markets-section">
        <div className="container">
          <motion.div 
            className="section-header"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="section-badge">Live Markets</span>
            <h2>Popular Trading Pairs
              <span className="live-indicator">
                <span className="live-dot"></span>
                Live
              </span>
            </h2>
            <p>Real-time prices updated every 60 seconds</p>
          </motion.div>

          {/* Market Tabs */}
          <div className="market-tabs">
            <button 
              className={`market-tab ${activeTab === 'all' ? 'active' : ''}`}
              onClick={() => handleTabChange('all')}
            >
              All
            </button>
            <button 
              className={`market-tab ${activeTab === 'gainers' ? 'active' : ''}`}
              onClick={() => handleTabChange('gainers')}
            >
              <TrendingUp size={14} />
              Top Gainers
            </button>
            <button 
              className={`market-tab ${activeTab === 'losers' ? 'active' : ''}`}
              onClick={() => handleTabChange('losers')}
            >
              <TrendingDown size={14} />
              Top Losers
            </button>
            <button 
              className={`market-tab ${activeTab === 'new' ? 'active' : ''}`}
              onClick={() => handleTabChange('new')}
            >
              ✨ New
            </button>
            <button 
              className={`market-tab ${activeTab === 'popular' ? 'active' : ''}`}
              onClick={() => handleTabChange('popular')}
            >
              🔥 Most Popular
            </button>
          </div>

          <div className="markets-table">
            <div className="markets-header">
              <span>Pair</span>
              <span>Price</span>
              <span>24h Change</span>
              <span className="hide-mobile">Volume</span>
              <span>Trade</span>
            </div>
            {isLoading ? (
              <div className="markets-loading">
                <Loader className="spin" size={24} />
                <span>Loading live prices...</span>
              </div>
            ) : (
              tradingPairs.map((pair, index) => (
                <motion.div
                  key={pair.pair}
                  className="market-row"
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                >
                  <div className="pair-info">
                    <img src={pair.logo} alt={pair.pair} className="pair-logo" />
                    <span className="pair-name">{pair.pair}</span>
                  </div>
                  <span className="pair-price">${pair.price}</span>
                  <span className={`pair-change ${pair.isUp ? 'up' : 'down'}`}>
                    {pair.isUp ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
                    {pair.change}
                  </span>
                  <span className="pair-volume hide-mobile">${pair.volume}</span>
                  <button 
                    className="btn btn-sm btn-trade"
                    onClick={() => setIsModalOpen(true)}
                  >
                    Trade
                  </button>
                </motion.div>
              ))
            )}
          </div>

          {hasMoreCoins && (
            <motion.div 
              className="view-all-markets"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
            >
              <button 
                className="btn btn-outline"
                onClick={handleLoadMore}
                disabled={isLoadingMore}
              >
                {isLoadingMore ? (
                  <>
                    <Loader className="spin" size={18} />
                    Loading...
                  </>
                ) : (
                  <>
                    View More Markets ({filteredCoins.length - visibleCoins} more)
                    <ChevronRight size={18} />
                  </>
                )}
              </button>
            </motion.div>
          )}
        </div>
      </section>

      {/* Trading Features Section */}
      <section className="trading-features-section">
        <div className="container">
          <motion.div 
            className="section-header"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="section-badge">Trading Options</span>
            <h2>Multiple Ways to <span className="text-gradient">Trade</span></h2>
            <p>Choose the trading style that suits you best</p>
          </motion.div>

          <div className="trading-features-grid">
            {tradingFeatures.map((feature, index) => (
              <motion.div
                key={feature.title}
                className="trading-feature-card"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                {feature.tag && <span className="feature-tag">{feature.tag}</span>}
                <div className="feature-icon-box">
                  <feature.icon size={28} />
                </div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
                <button 
                  className="btn btn-ghost"
                  onClick={() => setIsModalOpen(true)}
                >
                  Start Trading
                  <ArrowRight size={16} />
                </button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Trade With Us Section */}
      <section className="why-trade-section">
        <div className="container">
          <motion.div 
            className="section-header"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="section-badge">Our Advantages</span>
            <h2>Why Trade With <span className="text-gradient">Web3 Ledger</span></h2>
            <p>Industry-leading features for serious traders</p>
          </motion.div>

          <div className="why-trade-grid">
            {whyTradeFeatures.map((feature, index) => (
              <motion.div
                key={feature.label}
                className="why-trade-card"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="why-trade-icon">
                  <feature.icon size={24} />
                </div>
                <div className="why-trade-value">{feature.value}</div>
                <div className="why-trade-label">{feature.label}</div>
                <p className="why-trade-desc">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Trading Tools Section */}
      <section className="trading-tools-section">
        <div className="container">
          <div className="tools-grid">
            <motion.div 
              className="tools-content"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="section-badge">Advanced Tools</span>
              <h2>Professional Trading<br /><span className="text-gradient">Tools & Charts</span></h2>
              <p>
                Get access to TradingView charts, real-time order books, 
                advanced order types, and powerful API for algorithmic trading.
              </p>
              
              <ul className="tools-list">
                <li>
                  <CandlestickChart size={20} />
                  <span>TradingView Integration</span>
                </li>
                <li>
                  <Activity size={20} />
                  <span>Real-time Order Book</span>
                </li>
                <li>
                  <Clock size={20} />
                  <span>Limit, Market & Stop Orders</span>
                </li>
                <li>
                  <Zap size={20} />
                  <span>High-Performance API</span>
                </li>
              </ul>

              <button 
                className="btn btn-primary btn-lg"
                onClick={() => setIsModalOpen(true)}
              >
                <Wallet size={20} />
                Connect & Trade
              </button>
            </motion.div>

            <motion.div 
              className="tools-visual"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="chart-preview">
                <div className="chart-header">
                  <div className="chart-pair">
                    <img 
                      src={btcPrice?.image || 'https://assets.coingecko.com/coins/images/1/small/bitcoin.png'} 
                      alt="BTC" 
                    />
                    <div className="chart-pair-info">
                      <span className="chart-pair-name">BTC/USDT</span>
                      <span className="chart-pair-label">Bitcoin</span>
                    </div>
                  </div>
                  <div className="chart-price-info">
                    <span className="chart-current-price">
                      ${btcPrice ? btcPrice.current_price.toLocaleString('en-US', { minimumFractionDigits: 2 }) : '--'}
                    </span>
                    <span className={`chart-change ${btcPrice && btcPrice.price_change_percentage_24h >= 0 ? 'up' : 'down'}`}>
                      {btcPrice && btcPrice.price_change_percentage_24h >= 0 ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
                      {btcPrice ? `${btcPrice.price_change_percentage_24h >= 0 ? '+' : ''}${btcPrice.price_change_percentage_24h.toFixed(2)}%` : '--'}
                    </span>
                  </div>
                </div>
                
                <div className="chart-body">
                  <div className="chart-grid">
                    <div className="grid-line"></div>
                    <div className="grid-line"></div>
                    <div className="grid-line"></div>
                    <div className="grid-line"></div>
                  </div>
                  <svg className="chart-svg" viewBox="0 0 400 150" preserveAspectRatio="none">
                    <defs>
                      <linearGradient id="chartGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                        <stop offset="0%" stopColor="rgba(34, 197, 94, 0.4)" />
                        <stop offset="100%" stopColor="rgba(34, 197, 94, 0)" />
                      </linearGradient>
                      <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="#6366f1" />
                        <stop offset="50%" stopColor="#22c55e" />
                        <stop offset="100%" stopColor="#22c55e" />
                      </linearGradient>
                    </defs>
                    {/* Area fill */}
                    <path
                      d="M0,120 Q20,110 40,100 T80,95 T120,80 T160,85 T200,70 T240,75 T280,55 T320,45 T360,40 T400,35 L400,150 L0,150 Z"
                      fill="url(#chartGradient)"
                      className="chart-area"
                    />
                    {/* Line */}
                    <path
                      d="M0,120 Q20,110 40,100 T80,95 T120,80 T160,85 T200,70 T240,75 T280,55 T320,45 T360,40 T400,35"
                      fill="none"
                      stroke="url(#lineGradient)"
                      strokeWidth="3"
                      strokeLinecap="round"
                      className="chart-line"
                    />
                    {/* Glow effect */}
                    <path
                      d="M0,120 Q20,110 40,100 T80,95 T120,80 T160,85 T200,70 T240,75 T280,55 T320,45 T360,40 T400,35"
                      fill="none"
                      stroke="#22c55e"
                      strokeWidth="8"
                      strokeLinecap="round"
                      opacity="0.2"
                      filter="blur(4px)"
                      className="chart-glow"
                    />
                    {/* End dot */}
                    <circle cx="400" cy="35" r="6" fill="#22c55e" className="chart-dot">
                      <animate attributeName="r" values="6;8;6" dur="2s" repeatCount="indefinite" />
                      <animate attributeName="opacity" values="1;0.7;1" dur="2s" repeatCount="indefinite" />
                    </circle>
                    <circle cx="400" cy="35" r="12" fill="#22c55e" opacity="0.3" className="chart-dot-glow">
                      <animate attributeName="r" values="12;16;12" dur="2s" repeatCount="indefinite" />
                      <animate attributeName="opacity" values="0.3;0.1;0.3" dur="2s" repeatCount="indefinite" />
                    </circle>
                  </svg>
                  <div className="chart-price-labels">
                    <span>${btcPrice ? Math.round(btcPrice.current_price * 1.02).toLocaleString() : '90K'}</span>
                    <span>${btcPrice ? Math.round(btcPrice.current_price).toLocaleString() : '88K'}</span>
                    <span>${btcPrice ? Math.round(btcPrice.current_price * 0.98).toLocaleString() : '86K'}</span>
                  </div>
                </div>

                <div className="chart-stats">
                  <div className="chart-stat-item">
                    <span className="stat-label">24h High</span>
                    <span className="stat-value">${btcPrice ? Math.round(btcPrice.current_price * 1.015).toLocaleString() : '--'}</span>
                  </div>
                  <div className="chart-stat-item">
                    <span className="stat-label">24h Low</span>
                    <span className="stat-value">${btcPrice ? Math.round(btcPrice.current_price * 0.965).toLocaleString() : '--'}</span>
                  </div>
                  <div className="chart-stat-item">
                    <span className="stat-label">24h Vol</span>
                    <span className="stat-value">$28.5B</span>
                  </div>
                </div>
                
                <div className="chart-footer">
                  <span>1H</span>
                  <span>4H</span>
                  <span className="active">1D</span>
                  <span>1W</span>
                  <span>1M</span>
                  <span>1Y</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="trading-cta" style={{ backgroundImage: `url('/CTA-button-BG.png')` }}>
        <div className="container">
          <motion.div 
            className="trading-cta-content"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2>Ready to start trading?</h2>
            <p>Join millions of traders and start your journey today.</p>
            <div className="cta-actions">
              <button 
                className="btn btn-primary btn-lg"
                onClick={() => setIsModalOpen(true)}
              >
                <Wallet size={20} />
                Connect Wallet
              </button>
              <Link to="/get-started" className="btn btn-secondary btn-lg">
                Create Account
                <ArrowRight size={20} />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />

      {/* Wallet Connect Modal */}
      <WalletConnectModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConnect={handleWalletConnect}
        mode="register"
      />
    </div>
  );
};

export default Trading;
